import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Typography,
  Spin,
  message,
  Tooltip,
  Badge,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  ProjectOutlined,
  DollarOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { getInformeGeneralPorProyecto } from "../../../services/informes/informeGeneralPorProyectoService";
import InformeProyectosPDF from "./InformeproyectosPDF";

const { Title, Text } = Typography;
const { Option } = Select;

const InformeGeneralProyecto = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedImplementador, setSelectedImplementador] = useState([]);
  const [filtroAvance, setFiltroAvance] = useState("");

  // Cargar datos
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await getInformeGeneralPorProyecto();
      if (response.estado) {
        setData(response.data);
        setFilteredData(response.data);
        message.success("Datos cargados correctamente");
      } else {
        message.error("Error al cargar los datos");
      }
    } catch (error) {
      message.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtrar datos
  useEffect(() => {
    let filtered = data;

    // Filtro por texto (código interno e implementador)
    if (searchText) {
      filtered = filtered.filter(
        (item) =>
          item.codigo_interno
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          item.implementador.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filtro por implementador específico
    if (selectedImplementador) {
      filtered = filtered.filter(
        (item) => 
          selectedImplementador.length === 0 ? true
          : selectedImplementador.includes(item.implementador)
      );
    }

    // Filtro por rango de avance
    if (filtroAvance) {
      filtered = filtered.filter((item) => {
        const avance = parseFloat(item.porcentaje_avance);
        switch (filtroAvance) {
          case "sin_inicio":
            return avance === 0;
          case "en_proceso":
            return avance > 0 && avance < 100;
          case "completado":
            return avance === 100;
          case "alto_avance":
            return avance >= 75;
          default:
            return true;
        }
      });
    }

    setFilteredData(filtered);
  }, [searchText, selectedImplementador, filtroAvance, data]);

  // Formatear números
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("es-CO").format(value);
  };

  // Obtener opciones únicas para filtros
  const implementadores = [...new Set(data.map((item) => item.implementador))];

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const totalPlanificado = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_planificado_total),
      0
    );
    const totalEntregadoSinIva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_entregado_sin_iva),
      0
    );
    const totalEntregadoConIva = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.valor_entregado_con_iva),
      0
    );
    const promedioAvance =
      filteredData.length > 0
        ? filteredData.reduce(
            (sum, item) => sum + parseFloat(item.porcentaje_avance),
            0
          ) / filteredData.length
        : 0;
    const totalProductosPlan = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.total_cantidad_plan),
      0
    );
    const totalProductosEntregados = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.total_cantidad_entregada),
      0
    );

    // Proyectos por estado
    const proyectosSinInicio = filteredData.filter(
      (item) => parseFloat(item.porcentaje_avance) === 0
    ).length;
    const proyectosEnProceso = filteredData.filter((item) => {
      const avance = parseFloat(item.porcentaje_avance);
      return avance > 0 && avance < 100;
    }).length;
    const proyectosCompletos = filteredData.filter(
      (item) => parseFloat(item.porcentaje_avance) === 100
    ).length;

    return {
      totalPlanificado,
      totalEntregadoSinIva,
      totalEntregadoConIva,
      promedioAvance,
      totalProductosPlan,
      totalProductosEntregados,
      totalProyectos: filteredData.length,
      proyectosSinInicio,
      proyectosEnProceso,
      proyectosCompletos,
    };
  };

  const estadisticas = calcularEstadisticas();

  // Exportar a Excel
  const exportToExcel = () => {
    try {
      // Preparar los datos para Excel
      const excelData = filteredData.map((row) => ({
        "Código Interno": row.codigo_interno,
        Implementador: row.implementador,
        "Cantidad Planificada": parseFloat(row.total_cantidad_plan),
        "Valor Planificado Total": parseFloat(row.valor_planificado_total),
        "Cantidad Asignada": parseInt(row.total_cantidad_asignada),
        "Cantidad Entregada": parseInt(row.total_cantidad_entregada),
        "Valor Entregado Sin IVA": parseFloat(row.valor_entregado_sin_iva),
        "Valor Entregado Con IVA": parseFloat(row.valor_entregado_con_iva),
        "Valor total IVA": parseFloat(
          row.valor_entregado_con_iva - row.valor_entregado_sin_iva
        ),
        "Porcentaje de Avance": parseFloat(row.porcentaje_avance),
      }));

      // Crear el libro de trabajo
      const workbook = XLSX.utils.book_new();

      // Crear la hoja de trabajo principal
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 15 }, // Código Interno
        { wch: 30 }, // Implementador
        { wch: 18 }, // Cantidad Planificada
        { wch: 20 }, // Valor Planificado Total
        { wch: 18 }, // Cantidad Asignada
        { wch: 18 }, // Cantidad Entregada
        { wch: 22 }, // Valor Entregado Sin IVA
        { wch: 22 }, // Valor Entregado Con IVA
        { wch: 22 }, // Valor total  IVA
        { wch: 18 }, // Porcentaje de Avance
      ];

      worksheet["!cols"] = columnWidths;

      // 👉 Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, "Informe");

      // Generar y descargar el archivo
      const fecha = new Date().toISOString().split("T")[0];
      const nombreArchivo = `informe_general_proyectos_${fecha}.xlsx`;

      XLSX.writeFile(workbook, nombreArchivo);

      message.success(
        `Archivo Excel "${nombreArchivo}" descargado correctamente`
      );
    } catch (error) {
      console.error("Error al generar el archivo Excel:", error);
      message.error("Error al generar el archivo Excel");
    }
  };

  // Definir columnas de la tabla
  const columns = [
    {
      title: "Proyecto",
      dataIndex: "codigo_interno",
      key: "codigo_interno",
      width: 120,
      fixed: "left",
      render: (text) => (
        <Tag color="blue" style={{ fontSize: "12px", fontWeight: "bold" }}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Implementador",
      dataIndex: "implementador",
      key: "implementador",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <Badge status="processing" text={text} />
        </Tooltip>
      ),
    },
    {
      title: "Planificación",
      children: [
        {
          title: "Cantidad",
          dataIndex: "total_cantidad_plan",
          key: "total_cantidad_plan",
          width: 120,
          align: "center",
          render: (text) => (
            <Text strong style={{ color: "#1890ff" }}>
              {formatNumber(text)}
            </Text>
          ),
        },
        {
          title: "Valor Total",
          dataIndex: "valor_planificado_total",
          key: "valor_planificado_total",
          width: 160,
          align: "right",
          render: (text) => (
            <Text strong style={{ color: "#52c41a" }}>
              {formatCurrency(text)}
            </Text>
          ),
        },
      ],
    },
    {
      title: "Ejecución",
      children: [
        {
          title: "Asignado",
          dataIndex: "total_cantidad_asignada",
          key: "total_cantidad_asignada",
          width: 100,
          align: "center",
          render: (text) => (
            <Text style={{ color: parseInt(text) > 0 ? "#722ed1" : "#bfbfbf" }}>
              {formatNumber(text)}
            </Text>
          ),
        },
        {
          title: "Entregado",
          dataIndex: "total_cantidad_entregada",
          key: "total_cantidad_entregada",
          width: 100,
          align: "center",
          render: (text) => (
            <Text
              strong
              style={{ color: parseInt(text) > 0 ? "#722ed1" : "#bfbfbf" }}
            >
              {formatNumber(text)}
            </Text>
          ),
        },
      ],
    },
    {
      title: "Valores Entregados",
      children: [
        {
          title: "Sin IVA",
          dataIndex: "valor_entregado_sin_iva",
          key: "valor_entregado_sin_iva",
          width: 150,
          align: "right",
          render: (text) => (
            <Text
              style={{ color: parseFloat(text) > 0 ? "#fa8c16"  : "#bfbfbf" }}
            >
              {formatCurrency(text)}
            </Text>
          ),
        },
        {
          title: "Con IVA",
          dataIndex: "valor_entregado_con_iva",
          key: "valor_entregado_con_iva",
          width: 150,
          align: "right",
          render: (text) => (
            <Text
              strong
              style={{ color: parseFloat(text) > 0 ? "#52c41a" : "#bfbfbf" }}
            >
              {formatCurrency(text)}
            </Text>
          ),
        },
        {
          title: "Total IVA",
          key: "valor_total_IVA",
          width: 140,
          align: "right",
          render: (text, record) => (
            <Text strong style={{ color: "#0958d9" }}>
              {formatCurrency(
                record.valor_entregado_con_iva - record.valor_entregado_sin_iva
              )}
            </Text>
          ),
        },
      ],
    },
    {
      title: "Avance",
      dataIndex: "porcentaje_avance",
      key: "porcentaje_avance",
      width: 120,
      align: "center",
      render: (text) => {
        const percentage = parseFloat(text);
        let status = "exception";
        let color = "#ff4d4f";

        if (percentage === 0) {
          status = "exception";
          color = "#bfbfbf";
        } else if (percentage === 100) {
          status = "success";
          color = "#52c41a";
        } else if (percentage >= 75) {
          status = "active";
          color = "#1890ff";
        } else if (percentage >= 25) {
          status = "active";
          color = "#fa8c16";
        }

        return (
          <Progress
            type="line"
            size={"small"}
            percent={percentage}
            status={status}
            strokeColor={color}
            format={() => `${percentage.toFixed(0)}%`}
          />
        );
      },
    },
  ];

  const dataPDF = {
    generadoEl: new Date().toLocaleDateString('es-CO'),
    resumen:estadisticas,
    proyectos: filteredData
  }
  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <Title level={2} style={{ marginBottom: "24px", color: "#1890ff" }}>
        📊 Informe General por Proyecto
      </Title>

      {/* Filtros y controles */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} lg={5}>
          <Select
                mode="multiple"
                placeholder="Filtrar por implementador"
                style={{ width: "100%" }}
                value={selectedImplementador}
                onChange={(values) => setSelectedImplementador(values)}
                allowClear
                showSearch
                optionFilterProp="children"
              >
              {implementadores.map((implementador) => (
                <Option key={implementador} value={implementador}>
                  {implementador}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Input
              placeholder="Buscar por código o implementador..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} lg={5}>
              <Select
                placeholder="Estado de avance"
                style={{ width: "100%" }}
                value={filtroAvance || undefined}
                onChange={(val) => setFiltroAvance(val ?? "")}
                allowClear
                showSearch
                optionFilterProp="children"
              >
              <Option value="sin_inicio">Sin Inicio (0%)</Option>
              <Option value="en_proceso">En Proceso (1-99%)</Option>
              <Option value="alto_avance">Alto Avance (≥75%)</Option>
              <Option value="completado">Completado (100%)</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={loadData}
                loading={loading}
              >
                Actualizar
              </Button>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                onClick={exportToExcel}
                disabled={filteredData.length === 0}
                style={{
                  backgroundColor: "#52c41a",
                  color: "#fff",
                  borderColor: "#52c41a",
                }}
              >
                Excel
              </Button>

              <InformeProyectosPDF data={dataPDF}></InformeProyectosPDF>
            </Space>
          </Col>
        </Row>
      </Card>
      {/* Tarjetas de estadísticas */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} lg={3}>
          <Card>
            <Statistic
              title="Total Proyectos"
              value={estadisticas.totalProyectos}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: "#1890ff", fontSize: "18px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Valor Planificado"
              value={estadisticas.totalPlanificado}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#52c41a", fontSize: "18px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Valor Entregado (Sin IVA)"
                  value={estadisticas.totalEntregadoSinIva}
                  formatter={(value) => formatCurrency(value)}
                  prefix="✅"
                  valueStyle={{ color: "#fa8c16", fontSize: "18px" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Valor Asignado (Con IVA)"
                  value={estadisticas.totalEntregadoConIva}
                  formatter={(value) => formatCurrency(value)}
                  prefix="📦"
                  valueStyle={{ color: "#52c41a", fontSize: "18px" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={3}>
          <Card>
            <Statistic
              title="Avance Promedio"
              value={estadisticas.promedioAvance}
              suffix="%"
              precision={1}
              prefix={<PercentageOutlined />}
              valueStyle={{ color: "#722ed1", fontSize: "18px" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#bfbfbf", fontSize: "14px" }}>
                  Sin Inicio
                </div>
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "24px",
                    fontWeight: "bold",
                  }}
                >
                  {estadisticas.proyectosSinInicio}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#fa8c16", fontSize: "14px" }}>
                  En Proceso
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "24px",
                    fontWeight: "bold",
                  }}
                >
                  {estadisticas.proyectosEnProceso}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#52c41a", fontSize: "14px" }}>
                  Completos
                </div>
                <div
                  style={{
                    color: "#52c41a",
                    fontSize: "24px",
                    fontWeight: "bold",
                  }}
                >
                  {estadisticas.proyectosCompletos}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabla de datos */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="codigo_interno"
            scroll={{ x: 1400, y: 600 }}
            pagination={{
              total: filteredData.length,
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["1", "15", "20", "50"], // 👈 aquí defines las opciones
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} productos`,
            }}
            size="small"
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text strong>TOTALES</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <Text strong style={{ color: "#1890ff" }}>
                      {formatNumber(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.total_cantidad_plan),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong style={{ color: "#52c41a" }}>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_planificado_total),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="center">
                    <Text strong style={{ color: "#722ed1" }}>
                      {formatNumber(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseInt(item.total_cantidad_asignada),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="center">
                    <Text strong style={{ color: "#fa8c16" }}>
                      {formatNumber(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseInt(item.total_cantidad_entregada),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text strong style={{ color: "#722ed1" }}>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_entregado_sin_iva),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <Text strong style={{ color: "#fa8c16" }}>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum + parseFloat(item.valor_entregado_con_iva),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="center">
                    <Text strong>
                      {formatCurrency(
                        filteredData.reduce(
                          (sum, item) =>
                            sum +
                            (parseFloat(item.valor_entregado_con_iva || 0) -
                              parseFloat(item.valor_entregado_sin_iva || 0)),
                          0
                        )
                      )}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default InformeGeneralProyecto;
